// src/components/shared/MotusLogo.js
//
// Product SVG logo — used in all nav bars. (Filename kept as a stable internal identifier; the wordmark text is the
// config-driven product brand — Voletus — not a literal.)
// Supports light and dark variants via the `variant` prop.
// Role-specific color via `accentColor` prop (blue=student, green=instructor, purple=org).

import React from 'react';
import { PRODUCT_NAME } from '../../config/product';

/**
 * @param {object} props
 * @param {number} [props.width=120] — logo width in px
 * @param {'light'|'dark'} [props.variant='light'] — background context
 * @param {string} [props.accentColor] — override gradient start color
 */
// Pass 87 §3 — the viewBox was sized for the 5-letter "motus" wordmark; the longer "voletus" crowded/clipped it
// on shared surfaces. Widen the canvas to 300 (icon/arc geometry unchanged) so the wordmark always has room, and
// derive height from the SAME aspect ratio so every caller's `width` scales correctly — fixed once here, no per-page hacks.
const VB_W = 300;
const VB_H = 60;

function MotusLogo({ width = 120, variant = 'light', accentColor }) {
  const height = Math.round(width * (VB_H / VB_W));
  const isDark = variant === 'dark';
  const textColor = isDark ? '#eceef5' : '#1a1d27';
  const trailColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(79,142,255,0.35)';

  // Default gradient: blue → cyan (student)
  const gradStart = accentColor || '#4f8eff';
  const gradEnd = accentColor === '#2ecc8a' ? '#00e5b4'
    : accentColor === '#a259ff' ? '#c070ff'
    : accentColor === '#F05A1F' ? '#FF8A50'
    : '#00d4ff';

  const gradId = `motus-grad-${Math.random().toString(36).slice(2, 6)}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${VB_W} ${VB_H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={gradStart} />
          <stop offset="100%" stopColor={gradEnd} />
        </linearGradient>
      </defs>
      {/* Secondary arc — trail */}
      <path d="M 10 50 Q 16 32 26 26 Q 36 20 44 28" stroke={trailColor} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      {/* Primary arc */}
      <path d="M 10 50 Q 14 22 28 14 Q 40 8 50 18" stroke={`url(#${gradId})`} strokeWidth="4.5" fill="none" strokeLinecap="round" />
      {/* Peak dot */}
      <circle cx="50" cy="18" r="6" fill={`url(#${gradId})`} />
      {/* Wordmark — stylised lowercase product brand (config-driven) */}
      <text x="68" y="41" fontFamily="'Syne','Bebas Neue',sans-serif" fontSize="30" fontWeight="800" fill={textColor} letterSpacing="-0.5">{String(PRODUCT_NAME || 'Voletus').toLowerCase()}</text>
    </svg>
  );
}

export default MotusLogo;
